import { useMemo, useState } from 'react';

// Third-party
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import {
  CardActions,
  CardMedia,
  Checkbox,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import CheckBoxOutlined from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlankOutlined';

// Components
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import Stack from '@mui/material/Stack';
import { SettingsWrapper } from '../../../layout';
import { SettingsHeader } from './SettingsHeader';

// Data
import { LANGUAGE_OPTIONS, THEME_OPTIONS } from '../data/settings';

// Hooks
import { useAppSelector } from '../../../hooks/useAppStore';

// Redux-toolkit
import { useDateFormatAPI, useSetLocaleAPI, useUpdateUserAPI } from '../../../hooks/api';
import { getAuthenticateUser, getTokenDetails } from '../../../store/authenticate/selectors';
import { BaseSelect } from '../../../components/base';
import { SYSTEM_FORMATS } from '../../../data';

const AppearanceSettings = () => {
  const theme = useTheme() as TCustomTheme;
  const { mutateAsync, isLoading } = useUpdateUserAPI();
  const { mutateAsync: setLocationMutate } = useSetLocaleAPI();
  const { mutateAsync: setDateFormatMutate } = useDateFormatAPI();

  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation('settings');
  const { isLight } = useCustomTheme(theme);
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { settings } = useAppSelector(getTokenDetails);
  const [toggleTheme, setToggleTheme] = useState(settings?.ui?.theme || 'light');
  const [language, setLanguage] = useState(settings?.ui.locale);
  const [format, setFormat] = useState(settings?.ui.dateFormat);

  const themeStyled = useMemo(() => ({
    border: isLight ? '1px solid rgb(241, 243, 244)' : '1px solid rgb(46, 50, 54)',
  }), [isLight]);

  const updateSettings = async () => {
    const data = {
      settings: {
        ui: {
          theme: toggleTheme || settings?.ui.theme,
          locale: language || settings?.ui.locale,
        },
      },
      name: personnel.name,
      phone: personnel.phone?.e164 || '',
    };

    await mutateAsync(data);
    await setLocationMutate({ locale: language || settings?.ui.locale });
    await setDateFormatMutate({
      dateFormat: (format || settings?.ui.dateFormat) as string,
    });
    await i18n.changeLanguage(language);

    enqueueSnackbar(`${t('settingsWasUpdate')}`, {
      variant: 'success',
    });
  };

  const isBtnDisabled = useMemo(() => toggleTheme === settings?.ui?.theme
    && language === settings?.ui.locale && format === settings?.ui.dateFormat, [toggleTheme, language, settings, format]);

  const handleFormChange = (val: string) => {
    setFormat(val);
  };

  return (
    <SettingsWrapper>
      <SettingsHeader
        title={t('appearance')}
        subtitle={t('changeHowManageMateLooks')}
      />
      <Divider sx={{
        pt: 4,
        mr: 4,
        borderStyle: 'dashed',
      }}
      />
      <Grid
        sx={{
          py: 4,
          paddingTop: { xs: 2 },
        }}
      >
        <Grid spacing={2} columns={12} container>
          <Grid size={{
            lg: 6,
            md: 5,
            xs: 12,
            sm: 12,
          }}
          >
            <Card
              sx={{
                mt: 6,
                marginTop: { xs: 2 },
                borderRadius: 2,
                ':hover': {
                  boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px',
                },
              }}
            >
              <CardHeader
                sx={{
                  p: 2,
                }}
                title={t('selectYourUI')}
                titleTypographyProps={{
                  variant: 'subtitle1',
                }}
              />
              <Divider sx={{ borderStyle: 'dashed' }} />
              <CardContent>
                <Grid justifyContent="space-between" spacing={4} container>
                  { THEME_OPTIONS.map((tm) => (
                    <Grid
                      key={tm.name}
                      size={{
                        lg: 6,
                        md: 12,
                        xs: 12,
                        sm: 6,
                      }}
                    >
                      <Card
                        onClick={() => setToggleTheme(tm.value)}
                        className="cursor-pointer"
                        sx={{
                          border: tm.value === toggleTheme
                            ? `1px solid ${theme.palette.primary.main}` : '',
                          p: 0.5,
                        }}
                        elevation={0}
                      >
                        <CardMedia
                          component="img"
                          loading="lazy"
                          alt={tm.name}
                          sx={{
                            ...themeStyled,
                            borderRadius: '8px',
                          }}
                          image={tm.src}
                        />
                        <Divider sx={{ borderStyle: 'dashed' }} />
                        <CardActions>
                          <Box
                            width="100%"
                            alignItems="center"
                            display="flex"
                            justifyContent="space-between"
                          >
                            <Typography>
                              {t(`${tm.name}`)}
                            </Typography>
                            <Checkbox
                              size="small"
                              value={tm.value}
                              onClick={() => {
                                setToggleTheme(tm.value);
                              }}
                              checked={tm.value === toggleTheme}
                              checkedIcon={<CheckBoxOutlined />}
                              icon={<CheckBoxOutlineBlank />}
                            />
                          </Box>
                        </CardActions>
                      </Card>
                    </Grid>
                  )) }
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{
            lg: 6,
            md: 7,
            xs: 12,
            sm: 12,
          }}
          >
            <Card
              sx={{
                ':hover': {
                  boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px',
                },
                mt: 6,
                marginTop: { xs: 2 },
                borderRadius: 2,
              }}
            >
              <CardHeader
                sx={{
                  p: 2,
                }}
                title={t('selectYourLanguage')}
                titleTypographyProps={{
                  variant: 'subtitle1',
                }}
              />
              <Divider sx={{ borderStyle: 'dashed' }} />
              <CardContent>
                <Box>
                  { LANGUAGE_OPTIONS.map((lang) => (
                    <Box mt={2} justifyContent="space-between" display="flex" alignItems="center" key={lang.value}>
                      <div className="align-center">
                        <img width={40} src={lang.src} alt={lang.key} />
                        <Typography mx={2}>{t(`${lang.key}`)}</Typography>
                      </div>
                      <Checkbox
                        size="small"
                        onClick={() => {
                          setLanguage(lang.value);
                        }}
                        value={lang.value}
                        checked={lang.value === language}
                        checkedIcon={<CheckBoxOutlined />}
                        icon={<CheckBoxOutlineBlank />}
                      />
                    </Box>
                  )) }
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{
            lg: 6,
            md: 5,
            xs: 12,
            sm: 12,
          }}
          >
            <Card
              sx={{
                mt: 6,
                marginTop: { xs: 2 },
                borderRadius: 2,
                ':hover': {
                  boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px',
                },
              }}
            >
              <CardHeader
                sx={{
                  p: 2,
                }}
                title={t('selectYourShopSettings')}
                titleTypographyProps={{
                  variant: 'subtitle1',
                }}
              />
              <Divider sx={{ borderStyle: 'dashed' }} />
              <CardContent>
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('chooseYourPreferredDate')}</Typography>
                  <Box width={150}>
                    <BaseSelect
                      selectProps={{
                        items: SYSTEM_FORMATS,
                        variant: 'outlined',
                        defaultValue: format,
                        onChange: (e) => handleFormChange(e.target.value as string),
                      }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Box sx={{ my: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton disabled={isBtnDisabled} loading={isLoading} onClick={updateSettings} type="submit" variant="contained">
            {t('saveChanges')}
          </LoadingButton>
        </Box>
      </Grid>
    </SettingsWrapper>
  );
};

export {
  AppearanceSettings,
};
